import { useState, useCallback, useRef, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  type Connection,
  type Edge,
  type Node,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  ArrowLeft,
  Plus,
  Settings,
  Code,
  BarChart2,
  Save,
  Globe,
  GripVertical,
  Trash2,
  Copy,
  CheckCheck,
  Loader2,
  ChevronRight,
  AlignLeft,
  AlignJustify,
  AtSign,
  Phone,
  Hash,
  ChevronDown,
  Circle,
  CheckSquare,
  Calendar,
  Clock,
  Sparkles,
  Image,
  MinusSquare,
  Type,
  FileText,
  Upload,
  PenLine,
  Star,
  Minus,
  LayoutTemplate,
  ZoomIn,
  GitBranch,
  Move,
  Eye,
} from "lucide-react";
import { SchemaFormRenderer } from "@/components/form-builder/SchemaFormRenderer";
import type { BookingForm, Region, ClientTypeRecord } from "@shared/schema";
import type {
  FormSchema,
  FormPage,
  FormField,
  FieldKind,
  ConditionalRule,
  FormTheme,
  FieldOption,
} from "@shared/form-builder-types";
import { createDefaultFormSchema } from "@shared/form-builder-types";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

// ── Palette definitions ───────────────────────────────────────────────────
interface PaletteField {
  kind: FieldKind;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  defaultLabel: string;
}

const PALETTE_GROUPS: { name: string; fields: PaletteField[] }[] = [
  {
    name: "Basic",
    fields: [
      { kind: "text", label: "Text", icon: AlignLeft, defaultLabel: "Short Text" },
      { kind: "textarea", label: "Paragraph", icon: AlignJustify, defaultLabel: "Long Text" },
      { kind: "email", label: "Email", icon: AtSign, defaultLabel: "Email Address" },
      { kind: "phone", label: "Phone", icon: Phone, defaultLabel: "Phone Number" },
      { kind: "number", label: "Number", icon: Hash, defaultLabel: "Number" },
    ],
  },
  {
    name: "Choice",
    fields: [
      { kind: "dropdown", label: "Dropdown", icon: ChevronDown, defaultLabel: "Select One" },
      { kind: "radio", label: "Radio", icon: Circle, defaultLabel: "Choose One" },
      { kind: "checkbox", label: "Checkbox", icon: CheckSquare, defaultLabel: "Check All That Apply" },
      { kind: "multi_select", label: "Multi-Select", icon: CheckSquare, defaultLabel: "Select Multiple" },
    ],
  },
  {
    name: "Date / Time",
    fields: [
      { kind: "date_picker", label: "Date Picker", icon: Calendar, defaultLabel: "Select Date" },
      { kind: "calendar_booking_slot", label: "Booking Slot", icon: Clock, defaultLabel: "Pick a Time Slot" },
    ],
  },
  {
    name: "Special",
    fields: [
      { kind: "event_offerings_picker", label: "Event Offerings", icon: Sparkles, defaultLabel: "Event Package" },
      { kind: "availability_calendar", label: "Availability Calendar", icon: Calendar, defaultLabel: "Preferred Date & Time" },
    ],
  },
  {
    name: "Layout",
    fields: [
      { kind: "heading", label: "Heading", icon: Type, defaultLabel: "Section Heading" },
      { kind: "divider", label: "Divider", icon: Minus, defaultLabel: "" },
      { kind: "image", label: "Image", icon: Image, defaultLabel: "" },
      { kind: "rich_text", label: "Rich Text", icon: FileText, defaultLabel: "Text Block" },
    ],
  },
  {
    name: "Advanced",
    fields: [
      { kind: "file_upload", label: "File Upload", icon: Upload, defaultLabel: "Upload File" },
      { kind: "signature", label: "Signature", icon: PenLine, defaultLabel: "Signature" },
      { kind: "rating", label: "Rating / Scale", icon: Star, defaultLabel: "Rating" },
    ],
  },
];

function makeId() {
  return Math.random().toString(36).slice(2, 10);
}

function findPaletteField(kind: FieldKind): PaletteField | undefined {
  for (const group of PALETTE_GROUPS) {
    const f = group.fields.find((f) => f.kind === kind);
    if (f) return f;
  }
}

// ── Custom PageNode for React Flow ────────────────────────────────────────
interface PageNodeData {
  page: FormPage;
  pageIndex: number;
  isSelected: boolean;
  onClick: (index: number) => void;
}

function PageNodeComponent({ data }: { data: PageNodeData }) {
  const { page, pageIndex, isSelected, onClick } = data;
  return (
    <div
      className={`relative min-w-[200px] rounded-lg border-2 bg-card shadow-md cursor-pointer transition-all ${
        isSelected ? "border-primary" : "border-border hover:border-primary/50"
      }`}
      onClick={() => onClick(pageIndex)}
      data-testid={`canvas-page-node-${pageIndex}`}
    >
      <Handle type="target" position={Position.Left} className="!bg-primary !w-3 !h-3" />
      <div className="p-3">
        <div className="flex items-center gap-2">
          <LayoutTemplate className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium text-sm truncate max-w-[130px]">
            {page.title || `Page ${pageIndex + 1}`}
          </span>
          <Badge variant="secondary" className="text-xs ml-auto shrink-0">
            {page.fields.length} fields
          </Badge>
        </div>
        {(page.conditionalRules?.length ?? 0) > 0 && (
          <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <GitBranch className="w-3 h-3" />
            {page.conditionalRules!.length} condition{page.conditionalRules!.length !== 1 ? "s" : ""}
          </div>
        )}
      </div>
      <Handle type="source" position={Position.Right} className="!bg-primary !w-3 !h-3" />
    </div>
  );
}

const nodeTypes = { pageNode: PageNodeComponent };

// ── SortableFieldRow ──────────────────────────────────────────────────────
function SortableFieldRow({
  field,
  isSelected,
  onSelect,
  onDelete,
}: {
  field: FormField;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: field.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const palette = findPaletteField(field.kind);
  const Icon = palette?.icon ?? AlignLeft;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 p-2 rounded border cursor-pointer text-sm transition-colors ${
        isSelected
          ? "border-primary bg-primary/5"
          : "border-border hover:border-primary/40 bg-card"
      }`}
      onClick={onSelect}
      data-testid={`field-row-${field.id}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="text-muted-foreground hover:text-foreground cursor-grab"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
      <span className="truncate flex-1">{field.label || palette?.defaultLabel || field.kind}</span>
      {field.required && (
        <span className="text-destructive text-xs">*</span>
      )}
      <button
        className="text-muted-foreground hover:text-destructive"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        data-testid={`button-delete-field-${field.id}`}
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ── Field Properties Panel ────────────────────────────────────────────────
function FieldPropertiesPanel({
  field,
  allPages,
  currentPageIndex,
  onChange,
  onAddCondition,
}: {
  field: FormField;
  allPages: FormPage[];
  currentPageIndex: number;
  onChange: (updated: FormField) => void;
  onAddCondition: () => void;
}) {
  const isChoice = ["dropdown", "radio", "checkbox", "multi_select"].includes(field.kind);
  const isDatetime = ["date_picker", "calendar_booking_slot"].includes(field.kind);

  const updateOption = (i: number, label: string) => {
    const opts = [...(field.options ?? [])];
    opts[i] = { ...opts[i], label, value: label.toLowerCase().replace(/\s+/g, "_") };
    onChange({ ...field, options: opts });
  };
  const addOption = () => {
    onChange({
      ...field,
      options: [...(field.options ?? []), { id: makeId(), label: "Option", value: "option" }],
    });
  };
  const removeOption = (i: number) => {
    const opts = [...(field.options ?? [])];
    opts.splice(i, 1);
    onChange({ ...field, options: opts });
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        <div className="space-y-1">
          <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Field Properties</Label>
          <Badge variant="outline" className="text-xs">{field.kind}</Badge>
        </div>

        {field.kind !== "divider" && field.kind !== "image" && (
          <div className="space-y-1">
            <Label htmlFor="field-label" className="text-sm">Label</Label>
            <Input
              id="field-label"
              value={field.label}
              onChange={(e) => onChange({ ...field, label: e.target.value })}
              data-testid="input-field-label"
            />
          </div>
        )}

        {["text", "textarea", "email", "phone", "number", "dropdown", "radio"].includes(field.kind) && (
          <div className="space-y-1">
            <Label htmlFor="field-placeholder" className="text-sm">Placeholder</Label>
            <Input
              id="field-placeholder"
              value={field.placeholder ?? ""}
              onChange={(e) => onChange({ ...field, placeholder: e.target.value })}
              data-testid="input-field-placeholder"
            />
          </div>
        )}

        {!["divider", "heading", "image", "rich_text"].includes(field.kind) && (
          <div className="space-y-1">
            <Label htmlFor="field-help" className="text-sm">Help Text</Label>
            <Input
              id="field-help"
              value={field.helpText ?? ""}
              onChange={(e) => onChange({ ...field, helpText: e.target.value })}
              placeholder="Optional hint for the user"
              data-testid="input-field-help"
            />
          </div>
        )}

        {!["divider", "heading", "image", "rich_text"].includes(field.kind) && (
          <div className="flex items-center gap-2">
            <Switch
              id="field-required"
              checked={!!field.required}
              onCheckedChange={(v) => onChange({ ...field, required: v })}
              data-testid="switch-field-required"
            />
            <Label htmlFor="field-required" className="text-sm cursor-pointer">Required</Label>
          </div>
        )}

        {["text", "textarea"].includes(field.kind) && (
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Min length</Label>
              <Input
                type="number"
                value={field.minLength ?? ""}
                onChange={(e) => onChange({ ...field, minLength: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Max length</Label>
              <Input
                type="number"
                value={field.maxLength ?? ""}
                onChange={(e) => onChange({ ...field, maxLength: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
          </div>
        )}

        {isChoice && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm">Options</Label>
              <Button size="sm" variant="outline" onClick={addOption} data-testid="button-add-option">
                <Plus className="w-3 h-3 mr-1" />Add
              </Button>
            </div>
            <div className="space-y-1">
              {(field.options ?? []).map((opt, i) => (
                <div key={opt.id} className="flex gap-1 items-center">
                  <Input
                    value={opt.label}
                    onChange={(e) => updateOption(i, e.target.value)}
                    className="flex-1 h-8 text-sm"
                    data-testid={`input-option-${i}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => removeOption(i)}
                    data-testid={`button-remove-option-${i}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="allow-other"
                checked={!!field.allowOther}
                onCheckedChange={(v) => onChange({ ...field, allowOther: !!v })}
              />
              <Label htmlFor="allow-other" className="text-sm cursor-pointer">Allow "Other" option</Label>
            </div>
          </div>
        )}

        {isDatetime && (
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Min advance days</Label>
              <Input
                type="number"
                value={field.minAdvanceDays ?? ""}
                onChange={(e) => onChange({ ...field, minAdvanceDays: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Max advance days</Label>
              <Input
                type="number"
                value={field.maxAdvanceDays ?? ""}
                onChange={(e) => onChange({ ...field, maxAdvanceDays: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
          </div>
        )}

        {field.kind === "event_offerings_picker" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Switch
                id="show-price"
                checked={!!field.showPrice}
                onCheckedChange={(v) => onChange({ ...field, showPrice: v })}
              />
              <Label htmlFor="show-price" className="text-sm cursor-pointer">Show price</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="show-duration"
                checked={!!field.showDuration}
                onCheckedChange={(v) => onChange({ ...field, showDuration: v })}
              />
              <Label htmlFor="show-duration" className="text-sm cursor-pointer">Show duration</Label>
            </div>
          </div>
        )}

        {field.kind === "availability_calendar" && (
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-sm">Mode</Label>
              <Select
                value={field.availabilityMode ?? "master_schedule"}
                onValueChange={(v: "master_schedule" | "connected_calendar") =>
                  onChange({ ...field, availabilityMode: v })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="master_schedule">Master Schedule</SelectItem>
                  <SelectItem value="connected_calendar">Connected Calendar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs">Buffer before (min)</Label>
                <Input
                  type="number"
                  value={field.bufferBefore ?? ""}
                  onChange={(e) => onChange({ ...field, bufferBefore: e.target.value ? Number(e.target.value) : undefined })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Buffer after (min)</Label>
                <Input
                  type="number"
                  value={field.bufferAfter ?? ""}
                  onChange={(e) => onChange({ ...field, bufferAfter: e.target.value ? Number(e.target.value) : undefined })}
                />
              </div>
            </div>
          </div>
        )}

        {field.kind === "heading" && (
          <div className="space-y-1">
            <Label className="text-sm">Heading Level</Label>
            <Select
              value={String(field.headingLevel ?? 2)}
              onValueChange={(v) => onChange({ ...field, headingLevel: Number(v) as 1 | 2 | 3 })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">H1 — Large</SelectItem>
                <SelectItem value="2">H2 — Medium</SelectItem>
                <SelectItem value="3">H3 — Small</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {field.kind === "rating" && (
          <div className="space-y-1">
            <Label className="text-sm">Max rating</Label>
            <Input
              type="number"
              min={3}
              max={10}
              value={field.ratingMax ?? 5}
              onChange={(e) => onChange({ ...field, ratingMax: Number(e.target.value) })}
            />
          </div>
        )}

        {(isChoice || isDatetime) && (
          <div className="pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={onAddCondition}
              data-testid="button-add-condition"
            >
              <GitBranch className="w-3.5 h-3.5 mr-2" />
              Add Conditional Logic
            </Button>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

// ── Theming Panel ─────────────────────────────────────────────────────────
function ThemingPanel({
  theme,
  onChange,
}: {
  theme: FormTheme;
  onChange: (t: FormTheme) => void;
}) {
  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Theming</Label>

        <div className="space-y-1">
          <Label className="text-sm">Primary Color</Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={theme.primaryColor ?? "#2563eb"}
              onChange={(e) => onChange({ ...theme, primaryColor: e.target.value })}
              className="w-10 h-10 rounded border cursor-pointer"
              data-testid="input-theme-primary-color"
            />
            <Input
              value={theme.primaryColor ?? "#2563eb"}
              onChange={(e) => onChange({ ...theme, primaryColor: e.target.value })}
              className="flex-1"
              data-testid="input-theme-primary-color-hex"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-sm">Background Color</Label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={theme.backgroundColor ?? "#ffffff"}
              onChange={(e) => onChange({ ...theme, backgroundColor: e.target.value })}
              className="w-10 h-10 rounded border cursor-pointer"
              data-testid="input-theme-bg-color"
            />
            <Input
              value={theme.backgroundColor ?? "#ffffff"}
              onChange={(e) => onChange({ ...theme, backgroundColor: e.target.value })}
              className="flex-1"
              data-testid="input-theme-bg-color-hex"
            />
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-sm">Font Family</Label>
          <Select
            value={theme.fontFamily ?? "inter"}
            onValueChange={(v: any) => onChange({ ...theme, fontFamily: v })}
          >
            <SelectTrigger data-testid="select-theme-font">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="system">System Default</SelectItem>
              <SelectItem value="inter">Inter</SelectItem>
              <SelectItem value="poppins">Poppins</SelectItem>
              <SelectItem value="playfair">Playfair Display</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-sm">Border Radius</Label>
          <Select
            value={theme.borderRadius ?? "rounded"}
            onValueChange={(v: any) => onChange({ ...theme, borderRadius: v })}
          >
            <SelectTrigger data-testid="select-theme-radius">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sharp">Sharp</SelectItem>
              <SelectItem value="rounded">Rounded</SelectItem>
              <SelectItem value="pill">Pill</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <Label className="text-sm">Logo URL</Label>
          <Input
            value={theme.logoUrl ?? ""}
            onChange={(e) => onChange({ ...theme, logoUrl: e.target.value })}
            placeholder="https://..."
            data-testid="input-theme-logo"
          />
        </div>
      </div>
    </ScrollArea>
  );
}

// ── Analytics Panel ───────────────────────────────────────────────────────
function AnalyticsPanel({ formId }: { formId: string }) {
  const { data, isLoading } = useQuery<{
    views: number;
    submissions: number;
    completionRate: number;
    perPage: { pageIndex: number; pageTitle: string; count: number }[];
  }>({
    queryKey: ["/api/booking-forms", formId, "analytics"],
    queryFn: async () => {
      const res = await fetch(`/api/booking-forms/${formId}/analytics`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch analytics");
      return res.json();
    },
    enabled: !!formId && formId !== "new",
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-4 text-sm text-muted-foreground">
        No analytics data yet. Analytics will appear once your form receives visits.
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-6">
        <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Form Analytics</Label>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border p-3 text-center">
            <div className="text-2xl font-bold">{data.views}</div>
            <div className="text-xs text-muted-foreground">Views</div>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <div className="text-2xl font-bold">{data.submissions}</div>
            <div className="text-xs text-muted-foreground">Submissions</div>
          </div>
          <div className="rounded-lg border p-3 text-center">
            <div className="text-2xl font-bold">{data.completionRate}%</div>
            <div className="text-xs text-muted-foreground">Completion</div>
          </div>
        </div>
        {data.perPage.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Per-Page Drop-off</Label>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={data.perPage} layout="vertical">
                <XAxis type="number" hide />
                <YAxis
                  type="category"
                  dataKey="pageTitle"
                  width={80}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip />
                <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                  {data.perPage.map((_, i) => (
                    <Cell
                      key={i}
                      fill={`hsl(${220 - i * 20}, 60%, ${55 + i * 5}%)`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </ScrollArea>
  );
}

// ── Embed Dialog ──────────────────────────────────────────────────────────
function EmbedDialog({
  open,
  onClose,
  form,
}: {
  open: boolean;
  onClose: () => void;
  form: BookingForm | null;
}) {
  const [copied, setCopied] = useState<"iframe" | "script" | null>(null);

  if (!form) return null;

  const publicUrl = `${window.location.origin}/book/${
    form.slug
  }`;

  const iframeCode = `<iframe
  src="${publicUrl}"
  width="100%"
  height="650"
  frameborder="0"
  style="border:0; max-width:800px;"
  allowtransparency="true"
></iframe>
<script>
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'clarik:resize') {
      document.querySelector('iframe[src="${publicUrl}"]').style.height = e.data.height + 'px';
    }
  });
</script>`;

  const scriptCode = `<div id="clarik-form-${form.id}"></div>
<script>
(function(){
  var d=document,s=d.createElement('script');
  s.src='${window.location.origin}/embed.js';
  s.setAttribute('data-form-id','${form.id}');
  s.setAttribute('data-form-url','${publicUrl}');
  d.head.appendChild(s);
})();
</script>`;

  const copy = (text: string, which: "iframe" | "script") => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Embed Form</DialogTitle>
          <DialogDescription>Copy the embed code to add this form to any website.</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="iframe">
          <TabsList className="w-full">
            <TabsTrigger value="iframe" className="flex-1">iFrame</TabsTrigger>
            <TabsTrigger value="script" className="flex-1">JS Snippet</TabsTrigger>
          </TabsList>
          <TabsContent value="iframe" className="mt-3">
            <div className="relative">
              <pre className="bg-muted rounded-md p-3 text-xs overflow-auto max-h-48">{iframeCode}</pre>
              <Button
                size="sm"
                variant="outline"
                className="absolute top-2 right-2"
                onClick={() => copy(iframeCode, "iframe")}
                data-testid="button-copy-iframe"
              >
                {copied === "iframe" ? <CheckCheck className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </TabsContent>
          <TabsContent value="script" className="mt-3">
            <div className="relative">
              <pre className="bg-muted rounded-md p-3 text-xs overflow-auto max-h-48">{scriptCode}</pre>
              <Button
                size="sm"
                variant="outline"
                className="absolute top-2 right-2"
                onClick={() => copy(scriptCode, "script")}
                data-testid="button-copy-script"
              >
                {copied === "script" ? <CheckCheck className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// ── Conditional Logic Modal ───────────────────────────────────────────────
function ConditionalLogicModal({
  open,
  onClose,
  field,
  currentPageIndex,
  allPages,
  existingRules,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  field: FormField | null;
  currentPageIndex: number;
  allPages: FormPage[];
  existingRules: ConditionalRule[];
  onSave: (rules: ConditionalRule[]) => void;
}) {
  const [rules, setRules] = useState<ConditionalRule[]>(existingRules);

  useEffect(() => {
    setRules(existingRules);
  }, [existingRules, open]);

  if (!field) return null;

  const addRule = () => {
    const nextPage = allPages.findIndex((_, i) => i > currentPageIndex);
    setRules((r) => [
      ...r,
      {
        id: makeId(),
        fieldId: field.id,
        operator: "equals",
        value: "",
        targetPageIndex: nextPage >= 0 ? nextPage : 0,
      },
    ]);
  };

  const updateRule = (i: number, patch: Partial<ConditionalRule>) => {
    setRules((r) => r.map((rule, idx) => (idx === i ? { ...rule, ...patch } : rule)));
  };

  const removeRule = (i: number) => {
    setRules((r) => r.filter((_, idx) => idx !== i));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Conditional Logic</DialogTitle>
          <DialogDescription>
            Route users to a different page based on their answer to "{field.label}".
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {rules.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No conditions yet. Click "Add Condition" below.
            </p>
          )}
          {rules.map((rule, i) => (
            <div key={rule.id} className="border rounded-md p-3 space-y-2 bg-muted/30">
              <div className="flex items-center gap-2 text-sm font-medium">
                <GitBranch className="w-4 h-4" />
                If this field…
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Select
                  value={rule.operator}
                  onValueChange={(v: any) => updateRule(i, { operator: v })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equals">Equals</SelectItem>
                    <SelectItem value="not_equals">Not equals</SelectItem>
                    <SelectItem value="contains">Contains</SelectItem>
                    <SelectItem value="is_before">Is before</SelectItem>
                    <SelectItem value="is_after">Is after</SelectItem>
                    <SelectItem value="is_empty">Is empty</SelectItem>
                    <SelectItem value="is_not_empty">Is not empty</SelectItem>
                  </SelectContent>
                </Select>
                {!["is_empty", "is_not_empty"].includes(rule.operator) && (
                  <Input
                    value={rule.value}
                    onChange={(e) => updateRule(i, { value: e.target.value })}
                    placeholder="Value"
                    className="h-8 text-xs"
                    data-testid={`input-condition-value-${i}`}
                  />
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">→ Go to page</span>
                <Select
                  value={String(rule.targetPageIndex)}
                  onValueChange={(v) => updateRule(i, { targetPageIndex: Number(v) })}
                >
                  <SelectTrigger className="h-8 text-xs flex-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allPages.map((p, pi) =>
                      pi !== currentPageIndex ? (
                        <SelectItem key={pi} value={String(pi)}>
                          {p.title || `Page ${pi + 1}`}
                        </SelectItem>
                      ) : null
                    )}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => removeRule(i)}
                  data-testid={`button-remove-rule-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between pt-2">
          <Button variant="outline" size="sm" onClick={addRule} data-testid="button-add-cond-rule">
            <Plus className="w-3.5 h-3.5 mr-1" />Add Condition
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              onClick={() => { onSave(rules); onClose(); }}
              data-testid="button-save-conditions"
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Form Settings Drawer ──────────────────────────────────────────────────
interface FormSettingsData {
  name: string;
  slug: string;
  clientTypes: string[];
  regionIds: string[];
  calendarIds: string[];
  openAvailability: boolean;
  formTitle: string;
  formSubtitle: string;
  formDisclaimer: string;
  isActive: boolean;
}

function FormSettingsDrawer({
  open,
  onClose,
  form,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  form: BookingForm | null;
  onSave: (data: FormSettingsData) => void;
}) {
  const [data, setData] = useState<FormSettingsData>({
    name: "",
    slug: "",
    clientTypes: [],
    regionIds: [],
    calendarIds: [],
    openAvailability: false,
    formTitle: "",
    formSubtitle: "",
    formDisclaimer: "",
    isActive: true,
  });

  const { data: regions = [] } = useQuery<Region[]>({ queryKey: ["/api/regions"] });
  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({ queryKey: ["/api/client-types"] });

  useEffect(() => {
    if (form) {
      setData({
        name: form.name,
        slug: form.slug,
        clientTypes: form.clientTypes ?? [],
        regionIds: form.regionIds ?? [],
        calendarIds: form.calendarIds ?? [],
        openAvailability: form.openAvailability ?? false,
        formTitle: form.formTitle ?? "",
        formSubtitle: form.formSubtitle ?? "",
        formDisclaimer: form.formDisclaimer ?? "",
        isActive: form.isActive,
      });
    }
  }, [form, open]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-[400px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Form Settings</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <div className="space-y-1">
            <Label>Form Name *</Label>
            <Input
              value={data.name}
              onChange={(e) => setData((d) => ({ ...d, name: e.target.value }))}
              data-testid="input-settings-name"
            />
          </div>
          <div className="space-y-1">
            <Label>URL Slug *</Label>
            <Input
              value={data.slug}
              onChange={(e) => setData((d) => ({ ...d, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
              data-testid="input-settings-slug"
            />
          </div>
          <div className="space-y-1">
            <Label>Form Title Override</Label>
            <Input
              value={data.formTitle}
              onChange={(e) => setData((d) => ({ ...d, formTitle: e.target.value }))}
              data-testid="input-settings-form-title"
            />
          </div>
          <div className="space-y-1">
            <Label>Form Subtitle Override</Label>
            <Textarea
              value={data.formSubtitle}
              onChange={(e) => setData((d) => ({ ...d, formSubtitle: e.target.value }))}
              rows={2}
              data-testid="input-settings-form-subtitle"
            />
          </div>
          <div className="space-y-1">
            <Label>Disclaimer Override</Label>
            <Input
              value={data.formDisclaimer}
              onChange={(e) => setData((d) => ({ ...d, formDisclaimer: e.target.value }))}
              data-testid="input-settings-disclaimer"
            />
          </div>

          {clientTypes.filter((c) => c.isActive).length > 0 && (
            <div className="space-y-1">
              <Label className="text-sm">Client Types</Label>
              <div className="border rounded-md p-2 space-y-1 max-h-32 overflow-y-auto">
                {clientTypes.filter((c) => c.isActive).map((ct) => (
                  <label key={ct.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={data.clientTypes.includes(ct.name)}
                      onCheckedChange={(v) =>
                        setData((d) => ({
                          ...d,
                          clientTypes: v
                            ? [...d.clientTypes, ct.name]
                            : d.clientTypes.filter((t) => t !== ct.name),
                        }))
                      }
                    />
                    {ct.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {regions.length > 0 && (
            <div className="space-y-1">
              <Label className="text-sm">Region Restriction</Label>
              <div className="border rounded-md p-2 space-y-1 max-h-32 overflow-y-auto">
                {regions.map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={data.regionIds.includes(r.id)}
                      onCheckedChange={(v) =>
                        setData((d) => ({
                          ...d,
                          regionIds: v
                            ? [...d.regionIds, r.id]
                            : d.regionIds.filter((id) => id !== r.id),
                        }))
                      }
                    />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Switch
              id="settings-open-avail"
              checked={data.openAvailability}
              onCheckedChange={(v) => setData((d) => ({ ...d, openAvailability: v }))}
            />
            <Label htmlFor="settings-open-avail" className="cursor-pointer text-sm">
              Open availability (skip calendar check)
            </Label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="settings-active"
              checked={data.isActive}
              onCheckedChange={(v) => setData((d) => ({ ...d, isActive: v }))}
              data-testid="switch-settings-active"
            />
            <Label htmlFor="settings-active" className="cursor-pointer text-sm">Form is active</Label>
          </div>

          <Button className="w-full" onClick={() => { onSave(data); onClose(); }} data-testid="button-save-settings">
            Save Settings
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ── Main FormBuilderPage ──────────────────────────────────────────────────
export default function FormBuilderPage() {
  const { formId } = useParams<{ formId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();

  // Schema state
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // UI state
  const [selectedPageIndex, setSelectedPageIndex] = useState<number | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [rightPanel, setRightPanel] = useState<"properties" | "theme" | "analytics" | "preview">("theme");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [embedOpen, setEmbedOpen] = useState(false);
  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  const [unsavedGuardPending, setUnsavedGuardPending] = useState<(() => void) | null>(null);

  // React Flow
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Fetch form
  const { data: form, isLoading } = useQuery<BookingForm>({
    queryKey: ["/api/booking-forms", formId],
    queryFn: async () => {
      if (formId === "new") return null as any;
      const res = await fetch(`/api/booking-forms/${formId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Not found");
      return res.json();
    },
    enabled: !!formId,
  });

  // Initialize schema on load
  useEffect(() => {
    if (formId === "new") {
      setSchema(createDefaultFormSchema());
    } else if (form) {
      if (form.formSchemaJson) {
        try {
          setSchema(JSON.parse(form.formSchemaJson));
        } catch {
          setSchema(createDefaultFormSchema());
        }
      } else {
        setSchema(createDefaultFormSchema());
      }
    }
  }, [form, formId]);

  // Build React Flow nodes from schema pages
  useEffect(() => {
    if (!schema) return;
    const newNodes: Node[] = schema.pages.map((page, i) => ({
      id: `page-${i}`,
      type: "pageNode",
      position: { x: 80 + i * 260, y: 120 },
      data: {
        page,
        pageIndex: i,
        isSelected: selectedPageIndex === i,
        onClick: (idx: number) => {
          setSelectedPageIndex(idx);
          setSelectedFieldId(null);
          setRightPanel("properties");
        },
      },
    }));
    setNodes(newNodes);

    // Build edges from conditional rules
    const newEdges: Edge[] = [];
    schema.pages.forEach((page, i) => {
      (page.conditionalRules ?? []).forEach((rule) => {
        const edgeId = `cond-${i}-${rule.id}`;
        if (!newEdges.find((e) => e.id === edgeId)) {
          newEdges.push({
            id: edgeId,
            source: `page-${i}`,
            target: `page-${rule.targetPageIndex}`,
            label: `If ${rule.operator} "${rule.value}"`,
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: "#3b82f6" },
          });
        }
      });
      // Default next-page edge
      const defaultTarget = page.defaultNextPageIndex ?? i + 1;
      if (defaultTarget < schema.pages.length) {
        const edgeId = `default-${i}`;
        if (!newEdges.find((e) => e.id === edgeId)) {
          newEdges.push({
            id: edgeId,
            source: `page-${i}`,
            target: `page-${defaultTarget}`,
            markerEnd: { type: MarkerType.ArrowClosed },
            style: { stroke: "#94a3b8" },
          });
        }
      }
    });
    setEdges(newEdges);
  }, [schema, selectedPageIndex]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    []
  );

  // Autosave debounce
  const scheduleSave = useCallback(
    (newSchema: FormSchema) => {
      setIsDirty(true);
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        doSave(newSchema);
      }, 1500);
    },
    [formId, form]
  );

  const doSave = async (schemaToSave: FormSchema, extra?: Partial<BookingForm>) => {
    if (formId === "new" || !formId) return;
    setIsSaving(true);
    try {
      await apiRequest("PATCH", `/api/booking-forms/${formId}`, {
        formSchemaJson: JSON.stringify(schemaToSave),
        ...extra,
      });
      setIsDirty(false);
      queryClient.invalidateQueries({ queryKey: ["/api/booking-forms", formId] });
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const updateSchema = (newSchema: FormSchema) => {
    setSchema(newSchema);
    scheduleSave(newSchema);
  };

  const addPage = () => {
    if (!schema) return;
    const newPage: FormPage = {
      id: makeId(),
      title: `Page ${schema.pages.length + 1}`,
      fields: [],
    };
    const newSchema = { ...schema, pages: [...schema.pages, newPage] };
    updateSchema(newSchema);
    setSelectedPageIndex(schema.pages.length);
  };

  const deletePage = (index: number) => {
    if (!schema || schema.pages.length <= 1) return;
    const pages = schema.pages.filter((_, i) => i !== index);
    const newSchema = { ...schema, pages };
    updateSchema(newSchema);
    setSelectedPageIndex(Math.min(index, pages.length - 1));
    setSelectedFieldId(null);
  };

  const updatePage = (index: number, patch: Partial<FormPage>) => {
    if (!schema) return;
    const pages = schema.pages.map((p, i) => (i === index ? { ...p, ...patch } : p));
    updateSchema({ ...schema, pages });
  };

  const addFieldFromPalette = (kind: FieldKind) => {
    if (selectedPageIndex === null || !schema) return;
    const palette = findPaletteField(kind);
    const newField: FormField = {
      id: makeId(),
      kind,
      label: palette?.defaultLabel ?? kind,
      required: false,
      ...(kind === "event_offerings_picker" ? { showPrice: true, showDuration: true } : {}),
      ...(kind === "availability_calendar" ? { availabilityMode: "master_schedule" } : {}),
      ...([
        "dropdown", "radio", "checkbox", "multi_select",
      ].includes(kind)
        ? {
            options: [
              { id: makeId(), label: "Option 1", value: "option_1" },
              { id: makeId(), label: "Option 2", value: "option_2" },
            ],
          }
        : {}),
    };
    const page = schema.pages[selectedPageIndex];
    updatePage(selectedPageIndex, { fields: [...page.fields, newField] });
    setSelectedFieldId(newField.id);
    setRightPanel("properties");
  };

  const updateField = (fieldId: string, updated: FormField) => {
    if (selectedPageIndex === null || !schema) return;
    const page = schema.pages[selectedPageIndex];
    const fields = page.fields.map((f) => (f.id === fieldId ? updated : f));
    updatePage(selectedPageIndex, { fields });
  };

  const deleteField = (fieldId: string) => {
    if (selectedPageIndex === null || !schema) return;
    const page = schema.pages[selectedPageIndex];
    const fields = page.fields.filter((f) => f.id !== fieldId);
    updatePage(selectedPageIndex, { fields });
    if (selectedFieldId === fieldId) setSelectedFieldId(null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || selectedPageIndex === null || !schema) return;
    const page = schema.pages[selectedPageIndex];
    const oldIdx = page.fields.findIndex((f) => f.id === active.id);
    const newIdx = page.fields.findIndex((f) => f.id === over.id);
    if (oldIdx >= 0 && newIdx >= 0) {
      updatePage(selectedPageIndex, { fields: arrayMove(page.fields, oldIdx, newIdx) });
    }
  };

  const handleSaveSettings = async (settingsData: FormSettingsData) => {
    if (!formId || formId === "new") return;
    try {
      await apiRequest("PATCH", `/api/booking-forms/${formId}`, settingsData);
      queryClient.invalidateQueries({ queryKey: ["/api/booking-forms", formId] });
      toast({ title: "Settings saved" });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const handlePublish = async () => {
    if (!formId || formId === "new" || !schema) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    await doSave(schema, { isActive: true });
    toast({ title: "Form published!", description: "Your form is now live." });
    queryClient.invalidateQueries({ queryKey: ["/api/booking-forms", formId] });
  };

  const handleBack = () => {
    if (isDirty) {
      setUnsavedGuardPending(() => () => navigate("/admin"));
    } else {
      navigate("/admin");
    }
  };

  const selectedPage = selectedPageIndex !== null && schema ? schema.pages[selectedPageIndex] : null;
  const selectedField = selectedPage?.fields.find((f) => f.id === selectedFieldId) ?? null;

  const activeConditionRules = selectedField
    ? (selectedPage?.conditionalRules ?? []).filter((r) => r.fieldId === selectedField.id)
    : [];

  if (isLoading && formId !== "new") {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-background overflow-hidden">
      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-2 border-b bg-card shrink-0">
        <Button variant="ghost" size="icon" onClick={handleBack} data-testid="button-back-builder">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold text-sm truncate">
            {form?.name ?? (formId === "new" ? "New Form" : "Form Builder")}
          </h1>
          {isSaving && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" />Saving…
            </span>
          )}
          {isDirty && !isSaving && (
            <span className="text-xs text-amber-500">Unsaved changes</span>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setRightPanel("analytics")}
          data-testid="button-analytics"
        >
          <BarChart2 className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setEmbedOpen(true)}
          data-testid="button-embed"
        >
          <Code className="w-4 h-4 mr-1.5" />
          Embed
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSettingsOpen(true)}
          data-testid="button-form-settings"
        >
          <Settings className="w-4 h-4" />
        </Button>
        {schema && isDirty && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => schema && doSave(schema)}
            disabled={isSaving}
            data-testid="button-save-now"
          >
            <Save className="w-4 h-4 mr-1.5" />
            Save
          </Button>
        )}
        <Button
          size="sm"
          onClick={handlePublish}
          disabled={isSaving}
          data-testid="button-publish"
        >
          <Globe className="w-4 h-4 mr-1.5" />
          Publish
        </Button>
      </div>

      {/* ── Main layout ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Left Sidebar: Element Palette ── */}
        <div className="w-56 border-r bg-card flex flex-col shrink-0 overflow-hidden">
          <div className="px-3 py-2 border-b">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Elements</p>
          </div>
          <ScrollArea className="flex-1">
            <div className="p-2">
              <Accordion type="multiple" defaultValue={["Basic", "Special"]} className="w-full">
                {PALETTE_GROUPS.map((group) => (
                  <AccordionItem key={group.name} value={group.name} className="border-b-0">
                    <AccordionTrigger className="py-2 text-xs font-semibold text-muted-foreground uppercase hover:no-underline">
                      {group.name}
                    </AccordionTrigger>
                    <AccordionContent className="pb-1">
                      <div className="space-y-0.5">
                        {group.fields.map((pf) => {
                          const Icon = pf.icon;
                          return (
                            <button
                              key={pf.kind}
                              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors text-left"
                              onClick={() => addFieldFromPalette(pf.kind)}
                              onDoubleClick={() => addFieldFromPalette(pf.kind)}
                              data-testid={`palette-item-${pf.kind}`}
                            >
                              <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                              {pf.label}
                            </button>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </ScrollArea>
        </div>

        {/* ── Center: Canvas + Page Editor ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Flowchart canvas */}
          <div className="flex-1 relative" style={{ minHeight: 200, maxHeight: selectedPageIndex !== null ? "40%" : undefined }}>
            <div className="absolute inset-0">
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                className="bg-muted/20"
              >
                <Background />
                <Controls />
                <MiniMap nodeColor="#3b82f6" nodeStrokeWidth={2} />
              </ReactFlow>
            </div>
            {/* Canvas toolbar */}
            <div className="absolute top-3 left-3 flex gap-2 z-10">
              <Button
                size="sm"
                variant="secondary"
                onClick={addPage}
                data-testid="button-add-page"
              >
                <Plus className="w-3.5 h-3.5 mr-1" />
                Add Page
              </Button>
            </div>
          </div>

          {/* Page Editor Panel */}
          {selectedPage !== null && selectedPageIndex !== null && schema && (
            <div className="flex flex-col border-t bg-card overflow-hidden" style={{ height: "60%" }}>
              <div className="flex items-center gap-3 px-4 py-2 border-b shrink-0">
                <Input
                  value={selectedPage.title}
                  onChange={(e) => updatePage(selectedPageIndex, { title: e.target.value })}
                  className="flex-1 h-8 text-sm font-medium"
                  placeholder="Page Title"
                  data-testid="input-page-title"
                />
                <span className="text-xs text-muted-foreground">
                  {selectedPage.fields.length} field{selectedPage.fields.length !== 1 ? "s" : ""}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  disabled={schema.pages.length <= 1}
                  onClick={() => deletePage(selectedPageIndex)}
                  data-testid="button-delete-page"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
              <ScrollArea className="flex-1">
                <div className="p-3 space-y-1.5">
                  {selectedPage.fields.length === 0 ? (
                    <div className="text-center py-8 text-sm text-muted-foreground border-2 border-dashed rounded-lg">
                      Click an element in the left panel to add fields here
                    </div>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={selectedPage.fields.map((f) => f.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {selectedPage.fields.map((field) => (
                          <SortableFieldRow
                            key={field.id}
                            field={field}
                            isSelected={selectedFieldId === field.id}
                            onSelect={() => {
                              setSelectedFieldId(field.id);
                              setRightPanel("properties");
                            }}
                            onDelete={() => deleteField(field.id)}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {selectedPageIndex === null && (
            <div className="flex items-center justify-center p-8 text-sm text-muted-foreground border-t">
              Click a page node on the canvas to edit its fields
            </div>
          )}
        </div>

        {/* ── Right Sidebar: Properties / Theme / Analytics / Preview ── */}
        <div className={`border-l bg-card flex flex-col shrink-0 overflow-hidden transition-all ${rightPanel === "preview" ? "w-[420px]" : "w-72"}`}>
          <div className="flex border-b shrink-0">
            <button
              className={`flex-1 px-2 py-2 text-xs font-medium ${rightPanel === "properties" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
              onClick={() => setRightPanel("properties")}
              data-testid="tab-properties"
            >
              Properties
            </button>
            <button
              className={`flex-1 px-2 py-2 text-xs font-medium ${rightPanel === "theme" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
              onClick={() => setRightPanel("theme")}
              data-testid="tab-theme"
            >
              Theme
            </button>
            <button
              className={`flex-1 px-2 py-2 text-xs font-medium ${rightPanel === "analytics" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
              onClick={() => setRightPanel("analytics")}
              data-testid="tab-analytics"
            >
              Analytics
            </button>
            <button
              className={`flex-1 px-2 py-2 text-xs font-medium flex items-center justify-center gap-1 ${rightPanel === "preview" ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
              onClick={() => setRightPanel("preview")}
              data-testid="tab-preview"
            >
              <Eye className="w-3 h-3" />
              Preview
            </button>
          </div>
          <div className="flex-1 overflow-hidden">
            {rightPanel === "theme" && schema && (
              <ThemingPanel
                theme={schema.theme ?? {}}
                onChange={(t) => updateSchema({ ...schema, theme: t })}
              />
            )}
            {rightPanel === "analytics" && formId && formId !== "new" && (
              <AnalyticsPanel formId={formId} />
            )}
            {rightPanel === "analytics" && formId === "new" && (
              <div className="p-4 text-sm text-muted-foreground">
                Save the form first to see analytics.
              </div>
            )}
            {rightPanel === "properties" && selectedField && selectedPage && (
              <FieldPropertiesPanel
                field={selectedField}
                allPages={schema?.pages ?? []}
                currentPageIndex={selectedPageIndex ?? 0}
                onChange={(updated) => updateField(selectedField.id, updated)}
                onAddCondition={() => setConditionModalOpen(true)}
              />
            )}
            {rightPanel === "properties" && !selectedField && (
              <div className="p-4 text-sm text-muted-foreground">
                Select a field to edit its properties, or click a page node on the canvas to start adding fields.
              </div>
            )}
            {rightPanel === "preview" && schema && (
              <div className="flex flex-col h-full overflow-hidden">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 dark:bg-amber-950/30 border-b text-xs text-amber-700 dark:text-amber-400 shrink-0">
                  <Eye className="w-3 h-3 shrink-0" />
                  Live preview — updates as you design
                </div>
                <ScrollArea className="flex-1">
                  <div className="p-4">
                    <SchemaFormRenderer
                      key="__builder_preview__"
                      schema={schema}
                      formId="__preview__"
                      onSubmit={() => {}}
                      isSubmitting={false}
                    />
                  </div>
                </ScrollArea>
              </div>
            )}
            {rightPanel === "preview" && !schema && (
              <div className="p-4 text-sm text-muted-foreground">
                Loading form…
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      <FormSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        form={form ?? null}
        onSave={handleSaveSettings}
      />

      <EmbedDialog
        open={embedOpen}
        onClose={() => setEmbedOpen(false)}
        form={form ?? null}
      />

      {selectedField && selectedPage && (
        <ConditionalLogicModal
          open={conditionModalOpen}
          onClose={() => setConditionModalOpen(false)}
          field={selectedField}
          currentPageIndex={selectedPageIndex ?? 0}
          allPages={schema?.pages ?? []}
          existingRules={activeConditionRules}
          onSave={(rules) => {
            if (selectedPageIndex === null || !schema) return;
            const page = schema.pages[selectedPageIndex];
            const otherRules = (page.conditionalRules ?? []).filter(
              (r) => r.fieldId !== selectedField.id
            );
            updatePage(selectedPageIndex, {
              conditionalRules: [...otherRules, ...rules],
            });
          }}
        />
      )}

      {/* Unsaved changes guard */}
      {unsavedGuardPending && (
        <Dialog open onOpenChange={() => setUnsavedGuardPending(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Unsaved changes</DialogTitle>
              <DialogDescription>
                You have unsaved changes. Are you sure you want to leave?
              </DialogDescription>
            </DialogHeader>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setUnsavedGuardPending(null)}>
                Stay
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  unsavedGuardPending();
                  setUnsavedGuardPending(null);
                }}
              >
                Leave
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
